import type { RefObject } from 'react';
import type { IdCaptionType, IToString, IValue, SearchValueType } from './common-interfaces';
import { DynamicFormRequiredType } from './dynamic-form/dynamic-form.enums';
import type { CheckboxGroupResult } from './fields/checkbox-group/checkbox-group.component';
import type { DropDownResult } from './fields/drop-down/drop-down-types';
import type { JsonData } from './generic-types';
import { extendTailwindMerge } from 'tailwind-merge';
import type { ClassValue } from 'clsx';
import clsx from 'clsx';

export function hasClassNameRecursive(element: Element, className: string): boolean {
  // the typeof check says that className is always a string but that is not the case...when the element is an SVG
  // className is a different object so this check is needed.
  if (typeof element.className === 'string' && element.className.includes(className)) {
    return true;
  }

  return element.parentElement ? hasClassNameRecursive(element.parentElement, className) : false;
}

export function getAttributeValueRecursive(element: Element, attributeName: string): string | null {
  const attributeValue = element.getAttribute(attributeName);
  if (attributeValue === null) {
    return element.parentElement ? getAttributeValueRecursive(element.parentElement, attributeName) : null;
  }

  return attributeValue;
}

export function getElementByClassNameRecursive(element: Element, className: string): Element {
  if (typeof element.className === 'string' && element.className.includes(className)) {
    return element;
  }

  return element.parentElement ? getElementByClassNameRecursive(element.parentElement, className) : element;
}

export function getElementByTagNameRecursive(element: Element, tagName: string): Element {
  if (element.tagName === tagName.toUpperCase()) {
    return element;
  }

  return element.parentElement ? getElementByTagNameRecursive(element.parentElement, tagName) : element;
}

export function isEventOutsideTarget(event: Event, target: HTMLElement) {
  const eventElement = event.target as HTMLElement;

  if (eventElement) {
    return !target.isSameNode(eventElement) && !target.contains(eventElement);
  }

  return false;
}

export function isEventWithinTarget(event: Event, target: HTMLElement) {
  const testElement = event.target as HTMLElement;
  return testElement ? isElementWithinTarget(testElement, target) : false;
}

export function isElementWithinTarget(element: HTMLElement, target: HTMLElement) {
  return element.isSameNode(target) || element.contains(target);
}

export function elementHasAllStyles(element: HTMLElement, styles: Record<string, string | number>) {
  const styleEntries = Object.entries(styles);
  const elementStyles = forceAssert<Record<string, string>>(getComputedStyle(element));
  for (let i = 0, length = styleEntries.length; i < length; i++) {
    const [style, value] = styleEntries[i];

    if (typeof value === 'string' && value.indexOf(',') > -1) {
      const values = value.split(',').map((item) => item.trim());
      if (!values.includes(elementStyles[style])) {
        return false;
      }
    } else {
      if (elementStyles[style] !== value) {
        return false;
      }
    }
  }

  return true;
}

export function elementHasAnyStyle(element: HTMLElement, styles: Record<string, string | number>) {
  const styleEntries = Object.entries(styles);
  const elementStyles = forceAssert<Record<string, string>>(getComputedStyle(element));
  for (let i = 0, length = styleEntries.length; i < length; i++) {
    const [style, value] = styleEntries[i];

    if (typeof value === 'string' && value.indexOf(',') > -1) {
      const values = value.split(',').map((item) => item.trim());
      if (values.includes(elementStyles[style])) {
        return true;
      }
    } else {
      if (elementStyles[style] === value) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Recursively searches for a style and when it finds it calls an action method to determine if it is the style being
 * searched for. If it is then the value is returned to the caller.
 * @param {HTMLElement} element - The element to start searching at.
 * @param {string} style - The style to search for.
 * @param {(styleValue: string) => boolean} action - Determines if this is the correct style.
 * @return {string | null} - Returns either the value or null if nothing is found.
 */
export function getElementStyleValueRecursive(
  element: HTMLElement,
  style: string,
  action: (styleValue: string) => boolean
): string | null {
  const elementStyles = forceAssert<Record<string, string>>(getComputedStyle(element));
  if (elementStyles[style] && action(elementStyles[style])) {
    return elementStyles[style];
  }

  return element.parentElement && getElementStyleValueRecursive(element.parentElement, style, action);
}

export function getAllElementStyleValuesRecursive(
  element: HTMLElement,
  style: string,
  action: (styleValue: string) => boolean,
  currentValues: Array<string> = []
): Array<string> {
  const elementStyles = forceAssert<Record<string, string>>(getComputedStyle(element));
  if (elementStyles[style] && action(elementStyles[style])) {
    currentValues.push(elementStyles[style]);
  }

  return element.parentElement
    ? getAllElementStyleValuesRecursive(element.parentElement, style, action, currentValues)
    : currentValues;
}

export function getAllElementStyleValues(style: string, action: (styleValue: string) => boolean) {
  const allElements = Array.from(document.querySelectorAll('body *'));
  const foundStyles: Array<string> = [];

  for (let i = 0, length = allElements.length; i < length; i++) {
    const elementStyles = forceAssert<Record<string, string>>(getComputedStyle(allElements[i]));
    if (elementStyles[style] && action(elementStyles[style])) {
      foundStyles.push(elementStyles[style]);
    }
  }

  return foundStyles;
}

/**
 * Allows an element to be retrieved by searching for particular CSS properties.
 *
 * @param {HTMLElement} element - The element to start searching from.
 * @param {Record<string, string | number>} styles - The style(s) to search for; a comma can be used if multiple styles are allowed.
 * @param {boolean} matchAllStyles - If true then all styles must be present for an element to be considered a match (default false).
 * @param {function} isValidElement - An optional callback that validates if an element is accepted as matched or it's skipped.
 *
 * @return {HTMLElement} - Either the found element or the body tag.
 */
export function getElementByCssStylesRecursive(
  element: HTMLElement,
  styles: Record<string, string | number>,
  matchAllStyles = false,
  isValidElement?: (element: HTMLElement) => boolean
): HTMLElement {
  if (matchAllStyles && elementHasAllStyles(element, styles)) {
    if (!isValidElement || isValidElement?.(element) === true) {
      return element;
    }
  } else if (!matchAllStyles && elementHasAnyStyle(element, styles)) {
    if (!isValidElement || isValidElement?.(element) === true) {
      return element;
    }
  }

  return element.parentElement
    ? getElementByCssStylesRecursive(element.parentElement, styles, matchAllStyles, isValidElement)
    : element;
}

const topElementTag = (document.scrollingElement || document.documentElement).tagName;

export function getAllElementsByCssStylesRecursive(
  element: HTMLElement,
  styles: Record<string, string | number>,
  matchAllStyles = false,
  isValidElement?: (element: HTMLElement) => boolean
): Array<HTMLElement> {
  const elements: Array<HTMLElement> = [];

  if (matchAllStyles && elementHasAllStyles(element, styles)) {
    if (!isValidElement || isValidElement?.(element) === true) {
      elements.push(element);
    }
  } else if (!matchAllStyles && elementHasAnyStyle(element, styles)) {
    if (!isValidElement || isValidElement?.(element) === true) {
      elements.push(element);
    }
  } else if (element.tagName === topElementTag) {
    elements.push(element);
  }

  if (element.parentElement && element.tagName !== topElementTag) {
    elements.push(...getAllElementsByCssStylesRecursive(element.parentElement, styles, matchAllStyles, isValidElement));
  }

  return elements;
}

export function getBrowserLanguage() {
  return window.navigator.languages ? window.navigator.languages[0] : window.navigator.language;
}

export function lowerCaseFirstLetter(value: string) {
  return value.substring(0, 1).toLocaleLowerCase() + value.substring(1);
}

/**
 * Recursively searches through an array and its child arrays using a search function.
 * @param {Array<T>} items - The items to search through with the predicate.
 * @param {string} propertyName - The name of the array property to search through.
 * @param {(item: T) => boolean} predicate - The function to use to search for an item.
 * @return {T | undefined} Returns either the found item or undefined.
 */
export function findRecursive<T>(
  items: Array<T>,
  propertyName: string,
  predicate: (item: T) => boolean
): T | undefined {
  for (let i = 0, length = items.length; i < length; i++) {
    const item = items[i];
    if (predicate(item)) {
      return item;
    } else if (Array.isArray(forceAssert<Record<string, unknown>>(item)[propertyName])) {
      const foundChild = findRecursive(
        forceAssert<Record<string, unknown>>(item)[propertyName] as Array<T>,
        propertyName,
        predicate
      );
      if (foundChild) {
        return foundChild;
      }
    }
  }

  return undefined;
}

/**
 * Determines if an item is an object; this is used to type guard an unknown type.
 * @param object - The item to determine if it is an object.
 * @returns {object is Record<string, unknown>} A boolean indicating if the passed item is an object.
 */
export function isObject(object: unknown): object is Record<string, unknown> {
  return typeof object === 'object' && object !== null;
}

export function isPrimitive(item: unknown): item is string | number | Date | boolean | bigint {
  return (
    typeof item !== 'undefined' &&
    (typeof item === 'string' ||
      typeof item === 'number' ||
      typeof item === 'boolean' ||
      typeof item === 'bigint' ||
      item instanceof Date)
  );
}

export function isRefObject(object: unknown): object is RefObject<unknown> {
  return isObject(object) && Object.prototype.hasOwnProperty.call(object, 'current');
}

/**
 * Determines if an object is empty or not.
 * @param {Record<string, unknown>} object - The object to check.
 * @returns {boolean} Indicates of the object is empty.
 */
export function isEmptyObject(object: Record<string, unknown>) {
  return Object.keys(object).length === 0;
}

export function isArrayOf(array: unknown, typeName: string) {
  return Array.isArray(array) && array.length > 0 && array.every((item) => typeof item === typeName);
}

export function cloneObject<T>(object: T, objectOverrides: Partial<Record<keyof T, unknown>> = {}): T {
  return {
    ...object,
    ...objectOverrides,
  };
}

/**
 * Searches through an array of data object to find an item by a particular field.
 * @param {JsonData} data - The data object to search.
 * @param {string} searchField - The field to search on.
 * @param {SearchValueType} searchValue - The value to search for.
 * @returns {number} Returns -1 if not found otherwise returns the index where the item is found.
 */
export function findDataIndex(data: JsonData, searchField: string, searchValue: SearchValueType) {
  for (let i = 0, length = data.length; i < length; i++) {
    if (data[i][searchField] === searchValue) {
      return i;
    }
  }

  return -1;
}

export function detectIfTruncated(element: HTMLElement) {
  return element.offsetWidth < element.scrollWidth;
}

export function containsToString(item: unknown): item is IToString {
  return isObject(item) || (isPrimitive(item) && Object.prototype.hasOwnProperty.call(item, 'toString'));
}

export function containsValue(item: unknown): item is IValue {
  return isObject(item) && Object.prototype.hasOwnProperty.call(item, 'value');
}

export function showRequiredIndicator(required: DynamicFormRequiredType, readOnly: boolean) {
  return !readOnly && required === DynamicFormRequiredType.True;
}

export function tryParseInt(value: string) {
  try {
    return parseInt(value);
  } catch {
    return undefined;
  }
}

/**
 * Forcibly asserts an item to a particular type; sometimes when a type needs to be asserted as another type TypeScript
 * will make the user first assert the item as unknown and then to the actual type. This function is a descriptive way
 * to do that.
 * @param item - The item to force to another type.
 * @returns {T} The final type assertion.
 */
export const forceAssert = <T>(item: unknown): T => {
  return item as T;
};

export function isIdCaption(object: unknown): object is IdCaptionType {
  return (
    isObject(object) &&
    Object.prototype.hasOwnProperty.call(object, 'id') &&
    typeof object.id === 'number' &&
    Object.prototype.hasOwnProperty.call(object, 'caption') &&
    typeof object.caption === 'string'
  );
}

export function isArrayOfIdCaption(object: unknown): object is Array<IdCaptionType> {
  return Array.isArray(object) && object.length > 0 && object.every(isIdCaption);
}

export function forEachAdd<T>(items: Array<T>, addFunc: (item?: T) => number, startIndex: number, endIndex: number) {
  let sum = 0;
  const finalIndex = Math.min(items.length - 1, endIndex);
  for (let i = startIndex; i <= finalIndex; i++) {
    const item = items[i];
    sum += addFunc(item);
  }

  return sum;
}

export function isCheckboxGroupResult(item: unknown): item is CheckboxGroupResult {
  return (
    isObject(item) &&
    Object.prototype.hasOwnProperty.call(item, 'value') &&
    Object.prototype.hasOwnProperty.call(item, 'text') &&
    typeof (item as Record<string, unknown>).text === 'string'
  );
}

export function convertToDropDownResult(data: JsonData, valueField: string, textField: string, archivedField?: string) {
  const dropDownResults: Array<DropDownResult> = [];

  for (let i = 0, length = data.length; i < length; i++) {
    const item = data[i];
    dropDownResults.push({
      value: item[valueField],
      text: item[textField] as string,
      archived: archivedField ? !!item[archivedField] : false,
      originalData: item,
    });
  }

  return dropDownResults;
}

export function addPositionToOptions(option: DropDownResult[]) {
  return option.map((option, index) => {
    return {
      ...option,
      position: index,
    };
  });
}

const twMerge = extendTailwindMerge((config) => {
  Object.keys(config.classGroups).forEach((key) => {
    const value = config.classGroups[key];
    config.classGroups[key] = [...value, { odin: value }];
  });

  return config;
});

export function mergeStyling(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{8}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

/**
 * Returns a contrasting text color ('#fff' or '#000') for a given hex background color,
 * based on the perceived luminance (ITU-R BT.709-6).
 *
 * Accepts 3, 6, or 8-digit hex strings (with or without '#'). The alpha channel
 * in 8-digit hex values (e.g. '#ffffffaa') is ignored — only the RGB components
 * are used for the luminance calculation.
 *
 * @param hexColor A valid hex color string (e.g. '#ffffff', '#fff', '#ffffffaa')
 * @returns '#fff' for dark backgrounds, '#000' for light backgrounds
 */
export function getContrastTextColor(hexColor: string): string {
  // Normalise shorthand (#abc → #aabbcc)
  let hex = hexColor.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  // Take only the first 6 hex chars (ignore alpha if present)
  const rgb = parseInt(hex.substring(0, 6), 16);
  const red = (rgb >> 16) & 0xff;
  const green = (rgb >> 8) & 0xff;
  const blue = rgb & 0xff;
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue; // 0 … 255

  return luminance < 150 ? '#fff' : '#000';
}
